import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Input from '@Components/Input';
import AutosuggestDropdown from './AutosuggestInputDropdown';

function AutosuggestLiveRegion({ dropdownDisplayed, matchesCount }) {
  const [liveMessage, setLiveMessage] = useState('');

  useEffect(() => {
    let announcementTimeout;

    if (dropdownDisplayed && matchesCount > 0) {
      setLiveMessage('');
      announcementTimeout = setTimeout(() => {
        setLiveMessage(`${matchesCount} suggestion${matchesCount === 1 ? '' : 's'} available`);
      }, 1000);
    } else {
      setLiveMessage('');
    }

    return () => {
      clearTimeout(announcementTimeout);
    };
  }, [dropdownDisplayed, matchesCount]);

  return (
    <div aria-live="polite" aria-atomic="true" role="status" className="u-els-hide-visually">
      {liveMessage}
    </div>
  );
}

AutosuggestLiveRegion.propTypes = {
  dropdownDisplayed: PropTypes.bool,
  matchesCount: PropTypes.number.isRequired,
};

function AutosuggestInput({
  activeOptionIndex,
  idInput,
  classNameInput,
  groupBy,
  highlight,
  autofocus,
  inputProps = {},
  createdRef,
  dropdownDisplayed,
  matches,
  query,
  value = '',
  inputOnChange,
  autosuggestOnSelect,
  setActiveOptionIndex,
  clearButton = true,
  afterLabelContent,
  animatedPlaceholder = false,
  placeholder,
  buildQuery,
  onClear,
  onSearch,
  customInputRenderer,
  closeDropdownAndResetMatches,
}) {
  const listboxId = `${idInput}-listbox`;
  const activeOptionId = activeOptionIndex >= 0 ? `${listboxId}-option-${activeOptionIndex}` : undefined;

  const handleInputKeyDown = (event) => {
    inputProps?.onKeyDown?.(event);

    if (event.defaultPrevented) {
      return;
    }

    if (event.key === 'Tab') {
      closeDropdownAndResetMatches();
      return;
    }

    if (event.key !== 'Enter' || !dropdownDisplayed || activeOptionIndex < 0 || !matches[activeOptionIndex]) {
      return;
    }

    event.preventDefault();
    autosuggestOnSelect(activeOptionIndex);
  };

  const autosuggestInputProps = {
    ...inputProps,
    autoComplete: 'off',
    role: 'combobox',
    'aria-autocomplete': 'list',
    'aria-controls': listboxId,
    'aria-expanded': dropdownDisplayed ? 'true' : 'false',
    'aria-activedescendant': activeOptionId,
    onKeyDown: handleInputKeyDown,
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch();
      closeDropdownAndResetMatches();
    }
  };

  return (
    <div className="react-autosuggest-input" ref={createdRef}>
      <AutosuggestLiveRegion dropdownDisplayed={dropdownDisplayed} matchesCount={matches.length} />
      {window.BUILD?.getAutosuggestValidation?.() && value && value.indexOf('aspirin') >= 0 ? (
        <div className="aspirin-police">
          <img alt="aspirin" src={window.BUILD?.getAutosuggestValidation?.()} />
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          <div>Say "aspirin" again!</div>
        </div>
      ) : null}
      {customInputRenderer ? (
        customInputRenderer({
          afterLabelContent,
          autofocus,
          buildQuery,
          classNameInput,
          clearButton,
          handleSearch,
          idInput,
          inputOnChange,
          inputProps: autosuggestInputProps,
          onClear,
          placeholder,
          value,
        })
      ) : (
        <Input
          afterLabelContent={afterLabelContent}
          clearButton={clearButton}
          id={idInput}
          classNameInput={classNameInput}
          value={value}
          debounceDelay={0}
          onChange={inputOnChange}
          autofocus={autofocus}
          inputProps={autosuggestInputProps}
          animatedPlaceholder={animatedPlaceholder}
          placeholder={placeholder}
          buildQuery={buildQuery}
          onClear={onClear}
          onApply={handleSearch}
        />
      )}
      {dropdownDisplayed && (
        <AutosuggestDropdown
          activeOptionIndex={activeOptionIndex}
          groupBy={groupBy}
          highlight={highlight}
          listboxId={listboxId}
          matches={matches}
          onActiveOptionChange={setActiveOptionIndex}
          onSelect={autosuggestOnSelect}
          query={query}
        />
      )}
    </div>
  );
}

const MemoizedAutosuggestInput = React.memo(AutosuggestInput);

MemoizedAutosuggestInput.propTypes = {
  /* owner props */
  buildQuery: PropTypes.object,
  idInput: PropTypes.string,
  classNameInput: PropTypes.string,
  groupBy: PropTypes.string,
  highlight: PropTypes.bool,
  autofocus: PropTypes.bool,

  /* withCreatedRef HOC */
  createdRef: PropTypes.object.isRequired,
  /* withAllStateHandlers HOC */
  activeOptionIndex: PropTypes.number,
  dropdownDisplayed: PropTypes.bool,
  matches: PropTypes.array.isRequired,
  query: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setActiveOptionIndex: PropTypes.func,
  /* withInputOnChange HOC */
  inputOnChange: PropTypes.func.isRequired,
  /* withAutosuggestOnSelect HOC */
  autosuggestOnSelect: PropTypes.func.isRequired,
  animatedPlaceholder: PropTypes.bool,
  clearButton: PropTypes.bool,
  afterLabelContent: PropTypes.element,
  placeholder: PropTypes.string,
  onClear: PropTypes.func,
  onSearch: PropTypes.func,
  customInputRenderer: PropTypes.func,
  closeDropdownAndResetMatches: PropTypes.func,
  inputProps: PropTypes.object,
};

export default MemoizedAutosuggestInput;
